// Pure helpers for the wizard's unified schools list: in-list de-duplication and a
// completeness read for the per-row chip. Extracted so the intake rules are testable
// without mounting the step.

const trimLower = (value) => (value || '').toString().trim().toLowerCase();

// Fields a new school is expected to carry before it counts as "Complete". Optional
// characteristics (ages, UK-only fields, contact) are deliberately excluded.
export const REQUIRED_SCHOOL_FIELDS = ['Name', 'Urn', 'NumberOfPupil', 'Postcode', 'Country', 'SchoolType'];

// Is `candidate` a duplicate of something already in the list (by URN, else by
// case-insensitive trimmed name)? `existingUrns` covers URNs already picked as existing
// MatSchoolIds links. Returns 'urn' | 'name' | null. `excludeId` skips a row against
// itself when re-validating an edit.
export const findListDuplicate = (rows, candidate, existingUrns = [], excludeId = null) => {
  const candidateUrn = trimLower(candidate.Urn);
  const candidateName = trimLower(candidate.Name);

  if (candidateUrn && existingUrns.map((u) => trimLower(u)).includes(candidateUrn)) {
    return 'urn';
  }

  for (const row of rows) {
    if (row.id === excludeId) continue;
    if (candidateUrn && trimLower(row.values.Urn) === candidateUrn) return 'urn';
    if (candidateName && trimLower(row.values.Name) === candidateName) return 'name';
  }
  return null;
};

// Row-source semantics, shared by the payload builder, the step and the summary rail:
//  - an existing Welbee school attached by search, or an unedited GIAS pick, is a LINK
//    (goes into MatSchoolIds, not gated on completeness);
//  - a manual/file row, or a GIAS pick that was edited, is a NEW school (MatNewSchools).
export const isExistingLink = (row) => row.source === 'existing' || (row.source === 'gias' && !row.dirty);
export const isNewSchoolRow = (row) =>
  row.source === 'manual' || row.source === 'file' || (row.source === 'gias' && row.dirty === true);

// Completeness of a single new-school row for its chip.
export const schoolCompleteness = (values) => {
  const missing = REQUIRED_SCHOOL_FIELDS.filter((field) => {
    const value = values[field];
    return value === '' || value === null || value === undefined;
  });
  return { complete: missing.length === 0, missingCount: missing.length, missing };
};

// The chip label a row shows: duplicates first, then existing-link, then completeness.
export const rowChip = (row) => {
  if (row.duplicate) return { label: 'Duplicate', color: 'error' };
  if (row.source === 'existing') return { label: 'Attached', color: 'info' };
  const { complete, missingCount } = schoolCompleteness(row.values);
  if (complete) return { label: 'Complete', color: 'success' };
  return { label: `${missingCount} field${missingCount === 1 ? '' : 's'} missing`, color: 'warning' };
};

// Recompute the duplicate flag on every row after any list change.
export const withDuplicateFlags = (rows, existingUrns = []) =>
  rows.map((row, index) => {
    const others = rows.filter((_, i) => i !== index);
    const reason = findListDuplicate(others, row.values, existingUrns, row.id);
    return { ...row, duplicate: Boolean(reason), duplicateReason: reason };
  });
